import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Bell, Check, DollarSign, Plus, Receipt, Search, TriangleAlert, X, Download, Pencil, Trash2 } from "lucide-react";
import { BELT_CLASS } from "@/lib/belts";

function StatusPill({ status }) {
  const map = {
    active: { label: "Active", bg: "var(--green-bg)", fg: "var(--green)", bd: "var(--green-border)" },
    canceling: { label: "Canceling", bg: "rgba(244,185,66,0.14)", fg: "rgb(244,185,66)", bd: "rgba(244,185,66,0.35)" },
    past_due: { label: "Past due", bg: "rgba(255,80,80,0.14)", fg: "rgb(255,120,120)", bd: "rgba(255,80,80,0.35)" },
    inactive: { label: "Inactive", bg: "transparent", fg: "var(--text-dim)", bd: "var(--border)" },
    canceled: { label: "Canceled", bg: "transparent", fg: "var(--text-dim)", bd: "var(--border)" },
  };
  const c = map[status] || map.inactive;
  return (
    <span className="pill" style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }} data-testid={`billing-status-${status}`}>
      {c.label}
    </span>
  );
}

function DueBadge({ days }) {
  if (days === null || days === undefined) return <span className="label-tiny">—</span>;
  if (days < 0) return <span className="mono text-[13px]" style={{ color: "rgb(255,120,120)" }}>{Math.abs(days)}d overdue</span>;
  if (days === 0) return <span className="mono text-[13px]" style={{ color: "rgb(244,185,66)" }}>Due today</span>;
  if (days <= 3) return <span className="mono text-[13px]" style={{ color: "rgb(244,185,66)" }}>in {days}d</span>;
  return <span className="mono text-[13px]" style={{ color: "var(--text-dim)" }}>in {days}d</span>;
}

/** Modal: record an off-platform payment (cash / transfer / Yappy). */
function RecordPaymentModal({ student, onClose, onSaved }) {
  const [amount, setAmount] = useState(student.amount || 120);
  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/billing/record-payment", {
        student_id: student.student_id,
        amount: parseFloat(amount), method, paid_at: paidAt, note,
      });
      onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not record payment");
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "grid", placeItems: "center", zIndex: 60, padding: 16 }}>
      <form onSubmit={submit} className="card w-full" style={{ maxWidth: 460 }} data-testid="record-payment-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label" style={{ color: "var(--green)" }}>Record payment</div>
              <div className="text-[18px] font-bold uppercase tracking-tight mt-1">{student.name}</div>
              <div className="label-tiny mt-0.5">{student.email || "no email on file"}</div>
            </div>
            <button type="button" onClick={onClose} data-testid="record-close"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block label">Amount (USD)
              <input type="number" step="0.01" required min="0" className="input mt-1" value={amount}
                     onChange={e => setAmount(e.target.value)} data-testid="record-amount" />
            </label>
            <label className="block label">Method
              <select className="input mt-1" value={method} onChange={e => setMethod(e.target.value)} data-testid="record-method">
                <option value="cash">Cash</option>
                <option value="transfer">Bank transfer</option>
                <option value="yappy">Yappy</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>
          <label className="block label mt-3">Paid on
            <input type="date" className="input mt-1" value={paidAt} onChange={e => setPaidAt(e.target.value)} data-testid="record-paid-at" />
          </label>
          <label className="block label mt-3">Note (optional)
            <input className="input mt-1" value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Cash at front desk" data-testid="record-note" />
          </label>
          <div className="label-tiny mt-3" style={{ color: "var(--text-dim)" }}>
            A receipt email will be sent to the member if they have an email on file. Membership auto-renews 30 days after this date.
          </div>
          <button type="submit" disabled={busy} className="btn btn-primary w-full mt-5" data-testid="record-submit">
            {busy ? "Recording…" : "Record payment"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Modal: fix a mistake on a manually-recorded payment (cash/transfer/Yappy
 * only — Tilopay card rows are locked since they represent a real charge). */
function EditPaymentModal({ payment, onClose, onSaved }) {
  const [amount, setAmount] = useState(payment.amount);
  const [method, setMethod] = useState(payment.method || "cash");
  const [paidAt, setPaidAt] = useState(payment.paid_at || (payment.created_at || "").slice(0, 10));
  const [note, setNote] = useState(payment.note || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/billing/history/${payment.session_id}`, {
        amount: parseFloat(amount), method, paid_at: paidAt, note,
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not update payment");
      setBusy(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "grid", placeItems: "center", zIndex: 65, padding: 16 }} onClick={e => e.stopPropagation()}>
      <form onSubmit={submit} className="card w-full" style={{ maxWidth: 460 }} data-testid="edit-payment-modal">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="label" style={{ color: "var(--green)" }}>Edit payment</div>
            <button type="button" onClick={onClose} data-testid="edit-payment-close"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block label">Amount (USD)
              <input type="number" step="0.01" required min="0" className="input mt-1" value={amount}
                     onChange={e => setAmount(e.target.value)} data-testid="edit-payment-amount" />
            </label>
            <label className="block label">Method
              <select className="input mt-1" value={method} onChange={e => setMethod(e.target.value)} data-testid="edit-payment-method">
                <option value="cash">Cash</option>
                <option value="transfer">Bank transfer</option>
                <option value="yappy">Yappy</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>
          <label className="block label mt-3">Paid on
            <input type="date" className="input mt-1" value={paidAt} onChange={e => setPaidAt(e.target.value)} data-testid="edit-payment-paid-at" />
          </label>
          <label className="block label mt-3">Note (optional)
            <input className="input mt-1" value={note} onChange={e => setNote(e.target.value)} data-testid="edit-payment-note" />
          </label>
          <div className="label-tiny mt-3" style={{ color: "var(--text-dim)" }}>
            The member's renewal date will be recalculated from their full payment history after saving.
          </div>
          <button type="submit" disabled={busy} className="btn btn-primary w-full mt-5" data-testid="edit-payment-submit">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Right-side drawer showing a single student's full payment history and
 * management actions (Cancel subscription). */
function HistoryDrawer({ student, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadHistory = () => api.get("/billing/history", { params: { student_id: student.student_id } }).then(r => setData(r.data));
  useEffect(() => { loadHistory(); }, [student.student_id]);

  const cancel = async () => {
    const msg = `Cancel ${student.name}'s membership?\n\nThey'll keep training until their current period ends (${student.current_period_end?.slice(0, 10) || "N/A"}), then subscription will end automatically. This is reversible before end of period.`;
    if (!window.confirm(msg)) return;
    setCanceling(true);
    try {
      await api.post("/billing/cancel", { student_id: student.student_id });
      onChanged?.();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not cancel");
    } finally {
      setCanceling(false);
    }
  };

  const deletePayment = async (r) => {
    if (!window.confirm(`Delete this $${r.amount.toFixed(2)} ${r.method} payment? Their renewal date will be recalculated.`)) return;
    try {
      await api.delete(`/billing/history/${r.session_id}`);
      loadHistory();
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not delete payment");
    }
  };

  const canCancel = student.status === "active" || student.status === "past_due";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 55, display: "flex", justifyContent: "flex-end" }}
         onClick={onClose} data-testid="history-drawer-backdrop">
      <div onClick={e => e.stopPropagation()} className="card"
           style={{ width: "min(440px, 100vw)", height: "100vh", overflowY: "auto", borderRadius: 0, borderLeft: "1px solid var(--border)" }}
           data-testid="history-drawer">
        <div className="p-5 border-b sticky top-0 z-10" style={{ borderColor: "var(--border)", background: "var(--bg-card)" }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="label" style={{ color: "var(--green)" }}>Payment history</div>
              <div className="text-[18px] font-bold uppercase tracking-tight mt-1">{student.name}</div>
              {student.plan && <div className="label-tiny mt-0.5" data-testid="drawer-plan">{student.plan}</div>}
            </div>
            <button onClick={onClose} data-testid="history-close"><X size={18} /></button>
          </div>
          {data && (
            <div className="mt-3 label-tiny">
              <span className="mono">{data.count}</span> payments · lifetime <span className="mono" style={{ color: "var(--green)" }}>${data.total.toFixed(2)}</span>
            </div>
          )}
          {canCancel && (
            <button onClick={cancel} disabled={canceling}
                    className="btn btn-outline w-full mt-3"
                    style={{ borderColor: "rgba(255,80,80,0.35)", color: "rgb(255,120,120)" }}
                    data-testid="drawer-cancel-sub">
              {canceling ? "Canceling…" : "Cancel subscription (end of period)"}
            </button>
          )}
        </div>
        <div className="p-5">
          {!data && <div className="label">Loading…</div>}
          {data && data.rows.length === 0 && (
            <div className="label-tiny" style={{ color: "var(--text-dim)" }} data-testid="history-empty">No payments recorded yet.</div>
          )}
          {data && data.rows.map((r, i) => (
            <div key={r.session_id || i} className="py-3 border-b flex items-start gap-3" style={{ borderColor: "var(--border)" }} data-testid={`history-row-${i}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
                <Receipt size={16} style={{ color: "var(--green)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px]">${r.amount.toFixed(2)} <span className="label-tiny">USD</span></div>
                <div className="label-tiny mt-0.5">{r.paid_at || (r.created_at || "").slice(0, 10)} · {r.method || "tilopay"} · <span className="capitalize">{r.kind}</span></div>
                {r.note && <div className="label-tiny mt-0.5" style={{ color: "var(--text-dim)" }}>“{r.note}”</div>}
              </div>
              {r.kind === "manual" && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(r)} className="btn btn-outline" style={{ padding: "5px 7px" }}
                          title="Fix a mistake" data-testid={`history-edit-${i}`}>
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => deletePayment(r)} className="btn btn-outline" style={{ padding: "5px 7px", color: "rgb(255,120,120)", borderColor: "rgba(255,80,80,0.35)" }}
                          title="Delete this entry" data-testid={`history-delete-${i}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {editing && (
        <EditPaymentModal
          payment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadHistory(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

export default function Billing() {
  const [data, setData] = useState(null);
  const [reminding, setReminding] = useState({});
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recordFor, setRecordFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  const refresh = () => api.get("/billing/students").then(r => setData(r.data));
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.students.filter(s => {
      // Match the KPI classification: "Inactive" bucket is everything that
      // isn't active or past_due (i.e., includes inactive/canceled/null).
      if (statusFilter === "active" && s.status !== "active") return false;
      if (statusFilter === "past_due" && s.status !== "past_due") return false;
      if (statusFilter === "inactive" && (s.status === "active" || s.status === "past_due")) return false;
      if (!q) return true;
      return (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
    });
  }, [data, query, statusFilter]);

  if (!data) return <div className="p-8 label">Loading…</div>;

  const remind = async (student_id) => {
    setReminding(s => ({ ...s, [student_id]: true }));
    try {
      const r = await api.post("/billing/remind", { student_id });
      setReminding(s => ({ ...s, [student_id]: `sent:${r.data.sent}` }));
    } catch {
      setReminding(s => ({ ...s, [student_id]: "err" }));
    }
    setTimeout(() => setReminding(s => { const c = { ...s }; delete c[student_id]; return c; }), 3500);
  };

  const exportCsv = () => {
    const rows = [["Name", "Email", "Belt", "Amount", "Status", "Next due", "Last charge", "Last invoice"]];
    for (const s of filtered) {
      rows.push([
        s.name, s.email || "", s.belt || "", s.amount || 0, s.status,
        s.current_period_end || "", s.last_charge_at || "", s.latest_invoice_amount || "",
      ]);
    }
    const csv = rows.map(r => r.map(f => `"${String(f).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ludus-billing-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  const { totals, mock_mode } = data;
  const filterChip = (key, label, count) => (
    <button onClick={() => setStatusFilter(key)}
            data-testid={`filter-${key}`}
            className="pill"
            style={{
              padding: "6px 12px", cursor: "pointer",
              background: statusFilter === key ? "var(--green-bg)" : "var(--bg-card)",
              color: statusFilter === key ? "var(--green)" : "var(--text)",
              border: `1px solid ${statusFilter === key ? "var(--green-border)" : "var(--border)"}`,
            }}>
      {label} <span className="mono ml-1" style={{ opacity: 0.7 }}>{count}</span>
    </button>
  );

  return (
    <div data-testid="page-billing">
      <PageHeader section="Engage" title="Billing" right={
        <div className="flex items-center gap-3">
          <button onClick={exportCsv} className="btn btn-outline" data-testid="btn-export-csv">
            <Download size={13} /> CSV
          </button>
          <div className="mono text-[12px] label-tiny">{mock_mode ? "SANDBOX · Tilopay mock" : "LIVE · Tilopay"}</div>
        </div>
      } />

      <div className="p-4 md:p-8 space-y-6">
        {mock_mode && (
          <div className="card p-4 flex items-center gap-3" style={{ background: "rgba(244,185,66,0.06)", borderColor: "rgba(244,185,66,0.25)" }} data-testid="billing-sandbox-banner">
            <TriangleAlert size={18} style={{ color: "rgb(244,185,66)" }} />
            <div>
              <div className="font-semibold text-[13px]">Sandbox mode</div>
              <div className="label-tiny">Add production Tilopay keys and set <span className="mono">TILOPAY_MOCK=false</span> to go live.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-5" data-testid="stat-mrr">
            <div className="label mb-1">Monthly recurring</div>
            <div className="text-[24px] font-bold"><DollarSign size={16} className="inline mb-1"/>{totals.monthly_recurring.toLocaleString()}</div>
          </div>
          <div className="card p-5" data-testid="stat-active">
            <div className="label mb-1">Active</div>
            <div className="text-[24px] font-bold" style={{ color: "var(--green)" }}>{totals.active}</div>
          </div>
          <div className="card p-5" data-testid="stat-past-due">
            <div className="label mb-1">Past due</div>
            <div className="text-[24px] font-bold" style={{ color: totals.past_due > 0 ? "rgb(255,120,120)" : "var(--text-dim)" }}>{totals.past_due}</div>
          </div>
          <div className="card p-5" data-testid="stat-inactive">
            <div className="label mb-1">Inactive</div>
            <div className="text-[24px] font-bold" style={{ color: "var(--text-dim)" }}>{totals.inactive}</div>
          </div>
        </div>

        <div className="card p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <div className="label">Members · billing</div>
            <div className="flex gap-2 flex-wrap">
              {filterChip("all", "All", data.students.length)}
              {filterChip("active", "Active", totals.active)}
              {filterChip("past_due", "Past due", totals.past_due)}
              {filterChip("inactive", "Inactive", totals.inactive)}
            </div>
          </div>
          <div className="mb-4" style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 12, top: 12, color: "var(--text-dim)" }} />
            <input className="input" style={{ paddingLeft: 34 }} placeholder="Search by name or email"
                   value={query} onChange={e => setQuery(e.target.value)} data-testid="billing-search" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="billing-table">
              <thead>
                <tr className="text-left" style={{ color: "var(--text-dim)" }}>
                  <th className="label-tiny py-2">Student</th>
                  <th className="label-tiny py-2">Amount</th>
                  <th className="label-tiny py-2">Status</th>
                  <th className="label-tiny py-2">Next due</th>
                  <th className="label-tiny py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="py-6 label-tiny text-center" style={{ color: "var(--text-dim)" }} data-testid="billing-empty-results">No members match.</td></tr>
                )}
                {filtered.map(s => (
                  <tr key={s.student_id} className="border-t hover:bg-white/5 cursor-pointer"
                      style={{ borderColor: "var(--border)" }} data-testid={`billing-row-${s.student_id}`}
                      onClick={() => setHistoryFor(s)}>
                    <td className="py-3 pr-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
                             style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
                          {s.initials}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-[13px] truncate">{s.name}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`belt-bar ${BELT_CLASS[s.belt] || "belt-white"}`} style={{ width: 18, height: 5 }}/>
                            <span className="label-tiny truncate capitalize" data-testid={`row-belt-${s.student_id}`}>{s.belt || "white"} · {s.stripes ?? 0}</span>
                          </div>
                          <div className="label-tiny truncate mt-0.5" data-testid={`row-plan-${s.student_id}`}>{s.plan || "Custom"} · {s.email || "no email"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-2 mono text-[13px]">${s.amount}</td>
                    <td className="py-3 pr-2"><StatusPill status={s.status} /></td>
                    <td className="py-3 pr-2"><DueBadge days={s.days_until_due} /></td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-1.5" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setRecordFor(s)} className="btn btn-outline"
                                style={{ padding: "5px 10px", fontSize: 11 }}
                                data-testid={`btn-record-${s.student_id}`}
                                title="Record cash / transfer payment">
                          <Plus size={11}/> Payment
                        </button>
                        <button onClick={() => remind(s.student_id)}
                                disabled={s.status === "inactive" || !!reminding[s.student_id]}
                                className="btn btn-outline"
                                style={{ padding: "5px 10px", fontSize: 11 }}
                                data-testid={`btn-remind-${s.student_id}`}>
                          {reminding[s.student_id] === "sent:0" ? "No device"
                            : String(reminding[s.student_id] || "").startsWith("sent") ? <><Check size={11}/> Sent</>
                            : reminding[s.student_id] === "err" ? "Failed"
                            : <><Bell size={11}/> Remind</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="label-tiny mt-3" style={{ color: "var(--text-dim)" }}>
            Tap a row to view that member&apos;s payment history.
          </div>
        </div>
      </div>

      {recordFor && (
        <RecordPaymentModal
          student={recordFor}
          onClose={() => setRecordFor(null)}
          onSaved={() => { setRecordFor(null); refresh(); }}
        />
      )}
      {historyFor && (
        <HistoryDrawer
          student={historyFor}
          onClose={() => setHistoryFor(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
